import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { pick, cloneDeep } from 'lodash';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { WrapupPanelService } from './wrapup-panel.service';
import { WrapUp } from 'common/api/models/wrap-up.model';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { FormFieldComponent } from './form-field/form-field.component';

@Component({
  selector: 'app-wrap-up-panel',
  templateUrl: './wrap-up-panel.component.html',
  styleUrls: ['./wrap-up-panel.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class WrapUpPanelComponent implements OnInit {
  @Input()
  workItemId: string;

  @Input()
  isEnded: boolean;

  @Input()
  isClosed: boolean;

  @Input()
  type: WorkItemTypes;

  @Input()
  rawWrapups: WrapUp[];

  @Output()
  wrapUpChange = new EventEmitter<WrapUp[][]>();

  @Output()
  togglePanel: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('formComponent') formComponent: FormFieldComponent;

  public error;
  public formValidationRequired;
  public builtInWrapUps;
  public customWrapUps;
  public wrapUps;
  public originalBuiltInWrapUps;
  public originalCustomWrapUps;
  public saveInProgress;
  public saveCompleted;
  public requiredWrapUps;
  public titleTextKey;
  public backButtonTextKey;

  private originalWrapUps;

  constructor(private _wrapupPanelService: WrapupPanelService) {
    this.error = false;

    this.formValidationRequired = false;
    this.builtInWrapUps = [];
    this.customWrapUps = [];
    this.wrapUps = [];
    this.originalWrapUps = [];
    this.originalBuiltInWrapUps = [];
    this.originalCustomWrapUps = [];
    this.saveInProgress = false;
    this.saveCompleted = false;
    this.requiredWrapUps = false;
  }

  ngOnInit(): void {
    if (!this.workItemId) {
      throw new Error('Please specify workItemId!');
    }
    if (!this.type) {
      throw new Error('Please specify the type!');
    }
    this.getTextKeys(this.type);
    this.getWrapUps();
  }

  getTextKeys(type): void {
    const typeKey = bcEnums.workItem.typeKey[type];
    if (typeof typeKey === 'undefined') {
      throw new Error('No type key belongs to the given type');
    }

    this.titleTextKey = `bcWrapupPanel.title${typeKey}`;
    this.backButtonTextKey = `bcWrapupPanel.backTo${typeKey}`;
  }

  getWrapUps(): void {
    let builtInWrapUps = this.rawWrapups[0];
    let customWrapUps = this.rawWrapups[1];
    this.initWrapUps(builtInWrapUps);
    this.builtInWrapUps = builtInWrapUps;
    this.originalBuiltInWrapUps = cloneDeep(builtInWrapUps);

    if (customWrapUps) {
      this.initWrapUps(customWrapUps);
      this.customWrapUps = customWrapUps;
      this.originalCustomWrapUps = cloneDeep(customWrapUps);
      this.wrapUps = this.builtInWrapUps.concat(this.customWrapUps);
      this.originalWrapUps = this.originalBuiltInWrapUps.concat(this.originalCustomWrapUps);
    } else {
      this.wrapUps = this.builtInWrapUps;
      this.originalWrapUps = this.originalBuiltInWrapUps;
    }
  }

  initWrapUps(wrapUps): void {
    if (!this.requiredWrapUps) {
      const requiredWrapUps = wrapUps.filter(item => item.required === true);

      this.requiredWrapUps = requiredWrapUps.length > 0;
    }

    const dropdownWrapUps = wrapUps.filter(wrapUp => !!wrapUp.options);

    for (const wrapUp of dropdownWrapUps) {
      this._buildWrapUp(wrapUp);
    }
  }

  private _buildWrapUp(wrapUp): void {
    wrapUp.options.folders.forEach(folder => {
      folder.isFolder = true;
    });

    wrapUp.itemsFlatList = [];

    for (const field of wrapUp.options.fields) {
      this._buildFolderTreeUpwards(field, wrapUp.itemsFlatList, wrapUp.options.folders);
    }
  }

  private _buildFolderTreeUpwards(node, addedItems, parentNodes): void {
    if (addedItems.includes(node)) {
      return;
    }

    addedItems.push(node);

    if (node.parentFolderId) {
      const parentNode = parentNodes.find(itemNode => itemNode.id === node.parentFolderId);

      if (!parentNode) {
        throw new Error('Failed to build tree from wrapup items');
      }

      this._buildFolderTreeUpwards(parentNode, addedItems, parentNodes);
    }
  }

  private _didFormFieldsChange(formFields: WrapUp[]): boolean {
    return formFields.some((field, index) => {
      return field.value !== this.originalWrapUps[index].value;
    });
  }

  submitForm(formFields): void {
    const errors = new Map();
    let errorId;

    // Form validation
    if (this.formValidationRequired) {
      formFields.forEach(field => {
        errorId = '';

        // Create a unique ID to store errors for validation
        errorId = errorId.concat(field.id, field.category);

        if (field.required && !this.isFieldValueValid(field.value)) {
          field.error = true;
          errors.set(errorId, true);
        } else if (field.error) {
          field.error = false;
          errors.delete(errorId);
        }
      });
    }

    // Save wrap ups
    if (errors.size === 0) {
      if (formFields.length > 0 && this._didFormFieldsChange(formFields)) {
        this.saveWrapups(formFields).then(() => {
          if (!this.formValidationRequired) {
            this.togglePanel.emit();
          }
        });
      } else {
        this.togglePanel.emit();
      }
    }
  }

  saveWrapups(formFields): Promise<any> {
    if (formFields.length === 0) {
      return;
    }

    this.saveInProgress = true;

    const saveWrapUpPromises = this._wrapupPanelService.safeSaveWrapUp(this.type, this.workItemId, this.separateFormFields(formFields));

    return Promise.all(saveWrapUpPromises)
      .then(() => {
        this.error = false;
        this.saveCompleted = true;
        this.saveInProgress = false;
        this.originalCustomWrapUps = cloneDeep(this.customWrapUps);
        this.originalBuiltInWrapUps = cloneDeep(this.builtInWrapUps);
      })
      .catch(() => {
        this.wrapUpChange.emit([this.originalBuiltInWrapUps, this.originalCustomWrapUps]);
        this.error = true;
        this.saveCompleted = false;
        this.saveInProgress = false;
      });
  }

  separateFormFields(formFields) {
    const result = {
      filteredBuiltInFields: [],
      filteredCustomFields: []
    };

    formFields.forEach((formField) => {
      const field: any = pick(formField, ['id', 'value', 'category']);
      if (field.category === 'CUSTOM') {
        result.filteredCustomFields.push(field);
      } else {
        result.filteredBuiltInFields.push(field);
      }
    });

    return result;
  }

  isFieldValueValid(value) {
    // 0 is valid because of the first value of dropdowns and radioboxes
    return value === 0 || !!value;
  }

  setupValidation(isRequired) {
    this.formValidationRequired = isRequired;
  }
}
